import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '@/app/routes';
import { Button, ButtonVariants } from '@carboledger/ui';
import { EmptyState, LoadingOverlay } from '@/components/feedback';
import { useTranslations } from '@/hooks';
import {
  useGetContractFormQuery,
  useUnlinkContractShipmentMutation,
  useUpdateContractMutation,
} from '../api/contractsApi';
import { ContractForm } from '../components/ContractForm';
import { useContractFormOptions } from '../hooks';
import { EMPTY_LIST, removedLinksOf } from '@/lib/utils';

// Inbounds ▸ Contracts ▸ Edit.
export const EditContractPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module, contractId } = useParams();

  const {
    data: values,
    isLoading: isLoadingContract,
    isError,
  } = useGetContractFormQuery(contractId);
  const { options, sites, materials, getEquivalentUnit, isLoading } = useContractFormOptions();
  const [updateContract, { isLoading: isSubmitting }] = useUpdateContractMutation();

  const [unlinkContractShipment] = useUnlinkContractShipmentMutation();

  const goToContracts = useCallback(() => {
    navigate(ROUTES.contracts(module));
  }, [navigate, module]);

  /**
   * Removing a chip only marks it; Save is what unlinks. The update goes first, so a failed
   * save leaves every link intact — and Cancel leaves the contract exactly as it was.
   */
  const handleSubmit = useCallback(
    async (nextValues) => {
      const result = await updateContract({
        module,
        id: contractId,
        values: nextValues,
        equivalentUnit: getEquivalentUnit(nextValues),
      });
      if (result.error) return;

      const unlinked = await Promise.all(
        removedLinksOf(nextValues).map((shipmentId) =>
          unlinkContractShipment({ module, id: contractId, shipmentId }),
        ),
      );
      if (unlinked.some((one) => one.error)) return;
      goToContracts();
    },
    [updateContract, unlinkContractShipment, module, contractId, getEquivalentUnit, goToContracts],
  );

  if (isLoadingContract || isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isError || !values) {
    return (
      <EmptyState
        title={t('CONTRACTS_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToContracts}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <ContractForm
      isEdit
      title={t('CONTRACTS_EDIT_TITLE')}
      submitLabel={t('COMMON_SAVE')}
      initialValues={values}
      options={options}
      sites={sites}
      materials={materials}
      linkedShipments={values.linkedShipments ?? EMPTY_LIST}
      onSubmit={handleSubmit}
      onClose={goToContracts}
      isSubmitting={isSubmitting}
    />
  );
};
